// @flow
import React from 'react';
import styled from 'styled-components';
import { Button } from '@finect/tabular-core/Button';
import { Flex } from '@finect/tabular-core/Flex';
import { Tooltip } from '@finect/tabular-components/Tooltip';
import { HelpCircle } from '@finect/tabular-icons/helpCircle';
import { maxMediaQueries } from '@finect/tabular-utils/mediaQueries';
import { EntryEditor } from '@finect/tabular-draft-editor';
import type { UserType } from '../../types';
import { getFromLocal, updateLocal } from '../../utils';
import { Author, MentionedProducts } from '../';
import { EntryHeading, EntryContent } from './';

const PostEntryContent = styled.div`
  margin-top: 9px;
`;

const PostEntryBlock = styled.div`
  position: relative;
  border-radius: ${({ theme }) => theme.borderRadius};
  box-shadow: ${({ theme }) => theme.boxShadow};
  background: #fff;
`;

const ResponseBlock = styled(Flex)`
  padding: 0 30px 24px;

  ${maxMediaQueries.phablet`
    padding: 0 15px 24px;
  `};
`;

const Help = styled.div`
  position: absolute;
  top: -30px;
  right: 6px;
  color: ${({ theme }) => theme.colors.fontLL};

  ${maxMediaQueries.phone`
    display: none;
  `};
`;

type Props = {
  id?: string,
  name: string,
  placeholder: string,
  user: UserType,
  inputRef: Function,
  addAnswer: Function,
  focusEditor: Function,
  minHeight?: {
    size: number,
    apply: string
  }
};

type State = {
  hasContent: boolean,
  selectedProducts: Array<Object>
};

export class PostEntry extends React.Component<Props, State> {
  state = {
    hasContent: false,
    selectedProducts: getFromLocal('answerProducts') || []
  };

  addProduct = (newProduct: Object) => {
    const newProducts = [...this.state.selectedProducts];
    const pos = newProducts.findIndex((product) => {
      const id = (newProduct.entity && newProduct.entity.id) || newProduct.id;
      return product.entity.id === id;
    });

    if (pos === -1) {
      newProducts.push(newProduct);
      this.setState({ selectedProducts: newProducts });
      updateLocal('answerProducts', newProducts);
    }
  };

  removeProduct = (products: Array<Object>) => {
    const { selectedProducts } = this.state;

    // Nothing to delete
    if (!selectedProducts.length) return;
    if (products.length === selectedProducts.length) return;

    // All previous products have been removed
    if (!products.length) {
      this.setState({ selectedProducts: [] });
      return updateLocal('answerProducts', []);
    }

    // Remove deleted products
    const newProducts = selectedProducts.filter(product => products.includes(product.entity.id));
    updateLocal('answerProducts', newProducts);
    this.setState({ selectedProducts: newProducts });
  };

  handleChange = (content: string) => {
    const { hasContent } = this.state;
    if (content && !hasContent) this.setState({ hasContent: true });
    if (!content && hasContent) this.setState({ hasContent: false });
  };

  handleAdd = () => {
    this.props.addAnswer(this.state.selectedProducts);
  };

  render() {
    const { hasContent } = this.state;
    const { id, name, placeholder, user, inputRef, focusEditor, minHeight } = this.props;

    return (
      <PostEntryContent>
        <EntryHeading>
          <Author
            author={{
              image: user.avatarURL,
              name: user.firstName ? `${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}` : '',
              alias: user.username,
              url: user.profileURL,
              type: 'user',
              adviserAt: null,
              certification: null
            }}
            isUser={true}
            action="responde"
          />
        </EntryHeading>
        <PostEntryBlock id={id}>
          <EntryContent>
            <EntryEditor
              name={name}
              inputRef={inputRef}
              placeholder={placeholder}
              handleFocus={focusEditor}
              focusEditor={focusEditor}
              handleChange={this.handleChange}
              handleAddProduct={this.addProduct}
              handleRemoveProduct={this.removeProduct}
              decorators={['mentions', 'products', 'link', 'inlink', 'images']}
              minHeight={minHeight}
            />
            {this.state.selectedProducts.length ? <MentionedProducts products={this.state.selectedProducts} /> : null}
          </EntryContent>
          <Help>
            <Tooltip
              display="block"
              width="306px"
              from="right"
              tip={
                <span>
                  Menciona productos con $.
                  <br />
                  Menciona empresas con @.
                  <br />
                  Arrastra una imagen para añadirla.
                </span>
              }
            >
              <HelpCircle size="20" strokeWidth="1.6" />
            </Tooltip>
          </Help>
          <ResponseBlock justify="flex-end">
            <Tooltip
              disabled={hasContent}
              display="inline-block"
              width="198px"
              textAlign="center"
              tip="Escribe tu respuesta para poder publicar."
            >
              <Button secondary width="198px" onClick={this.handleAdd} disabled={!hasContent}>
                Publica tu respuesta
              </Button>
            </Tooltip>
          </ResponseBlock>
        </PostEntryBlock>
      </PostEntryContent>
    );
  }
}
